import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { MiTrabajo } from '@/lib/supabase/tipos-bd'

/**
 * El trabajo del operario.
 *
 * Sale de una función que devuelve solo lo suyo: al técnico no se le abre
 * ninguna tabla. Por eso esta consulta no lleva filtros —no hay nada que
 * filtrar—, y por eso no puede traer un precio aunque alguien lo pida.
 */
/*
 * Lo último que se supo del día. En localStorage y no en IndexedDB porque es
 * poco, se lee en el primer pintado y tiene que estar ya.
 */
const LLAVE_TRABAJO = 'mega.mi-trabajo'

function guardarTrabajo(trabajos: MiTrabajo[]) {
  try {
    localStorage.setItem(
      LLAVE_TRABAJO,
      JSON.stringify({ cuando: new Date().toISOString(), trabajos }),
    )
  } catch {
    /* Sin espacio: se pierde el modo sin señal, no el trabajo. */
  }
}

/**
 * Lo último que se supo del trabajo del día, tal cual quedó en el teléfono.
 *
 * Se exporta porque la geocerca local (src/lib/campo/geocercaLocal.ts) saca de
 * aquí el punto y el radio del sitio para poder avisar sin señal. Es la misma
 * lista, leída de la misma llave: dos copias se habrían desincronizado.
 */
export function leerTrabajoGuardado(): MiTrabajo[] | null {
  try {
    const crudo = localStorage.getItem(LLAVE_TRABAJO)
    if (!crudo) return null
    const { trabajos } = JSON.parse(crudo) as { cuando: string; trabajos: MiTrabajo[] }
    return Array.isArray(trabajos) ? trabajos : null
  } catch {
    return null
  }
}

/** De cuándo es lo guardado, para poder decirlo en la pantalla. */
export function cuandoSeGuardoElTrabajo(): string | null {
  try {
    const crudo = localStorage.getItem(LLAVE_TRABAJO)
    if (!crudo) return null
    return (JSON.parse(crudo) as { cuando: string }).cuando ?? null
  } catch {
    return null
  }
}

export function useMiTrabajo() {
  const consulta = useQuery({
    queryKey: ['mi-trabajo'],
    // En campo la pantalla se deja abierta. Medio minuto de frescura basta
    // para que aparezca sola una orden que le acaban de asignar.
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<MiTrabajo[]> => {
      /*
       * EL TRABAJO DEL DÍA, GUARDADO EN EL TELÉFONO.
       *
       * La app ya abre sin señal, pero abría para no enseñar nada: esta
       * consulta fallaba con «Failed to fetch» y la pantalla quedaba en un
       * error. El técnico necesita ver a dónde va, a qué hora y el teléfono
       * del cliente justamente cuando no hay cobertura.
       *
       * Se guarda lo último que se supo y se sirve cuando no hay red. No es
       * un dato fresco y la pantalla lo dice: puede que le hayan reasignado
       * una orden y aquí no se vea. Pero saber lo de esta mañana es
       * infinitamente más útil que una pantalla en rojo.
       */
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const guardado = leerTrabajoGuardado()
        if (guardado) return guardado
      }

      try {
        const { data, error } = await supabase.rpc('fn_mi_trabajo')

        if (error) throw new Error(mensajeDeError(error))

        const trabajos = ((data ?? []) as unknown as MiTrabajo[]).map((fila) => ({
          ...fila,
          latitud: fila.latitud === null ? null : Number(fila.latitud),
          longitud: fila.longitud === null ? null : Number(fila.longitud),
          horas_asignadas: fila.horas_asignadas === null ? null : Number(fila.horas_asignadas),
          horas_ejecutadas: Number(fila.horas_ejecutadas),
          horas_extra: Number(fila.horas_extra),
        }))

        guardarTrabajo(trabajos)
        return trabajos
      } catch (fallo) {
        // «Con señal» no es «el servidor contesta»: la raya que no transmite
        // es lo normal en obra. Antes de dar error, lo guardado.
        const guardado = leerTrabajoGuardado()
        if (guardado) return guardado
        throw fallo
      }
    },
  })

  return {
    trabajos: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}
